/**
 * The colours a chart is drawn with, resolved from the stylesheet.
 *
 * ECharts knows nothing about CSS custom properties, so a chart left to its own
 * defaults draws #333 text and pale grid lines. That is invisible on the dark
 * panel, which is how the dark theme came to be usable everywhere except inside
 * the charts. Reading the tokens out of the stylesheet keeps one source of
 * truth: the palette a legend swatch uses in CSS is the palette the series uses
 * on the canvas.
 *
 * Resolved themes are cached per name and shared. The object is handed to a
 * `useMemo` and an effect dependency list in ChartView, so a new object per
 * render would tear down and rebuild every chart on every parent render.
 */

export type ThemeName = "light" | "dark";

export interface ChartTheme {
  name: ThemeName;
  /** Series colours, in order. */
  palette: string[];
  /** Titles, legend entries and data labels. */
  ink: string;
  /** Axis labels, which sit a step quieter than the rest of the chart. */
  axis: string;
  /** Horizontal split lines. */
  grid: string;
  tooltipBg: string;
  tooltipInk: string;
  tooltipLine: string;
  /** The panel behind the chart. Donut slices are separated with it. */
  surface: string;
  /** False when the reader has asked for reduced motion. */
  animate: boolean;
}

/**
 * Used when the stylesheet cannot be read: jsdom resolves no custom properties,
 * and a chart drawn in grey during a test would be a confusing way to fail.
 * Mirrors the `:root` block in styles.css.
 */
const FALLBACK = {
  ink: "#33475b",
  axis: "#7b8fa3",
  grid: "#e6ecf2",
  surface: "#ffffff",
  tooltipBg: "#ffffff",
  tooltipInk: "#19324a",
  tooltipLine: "#d7e2ea",
  palette: [
    "#2e6fdf",
    "#12a594",
    "#e08a34",
    "#8b5cf6",
    "#d4537e",
    "#639922",
    "#e24b4a",
    "#0ea5b7",
  ],
};

function reduceMotion(): boolean {
  // jsdom has no matchMedia, and a missing preference is not a stated one.
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

function resolve(styles: CSSStyleDeclaration, token: string, fallback: string) {
  const value = styles.getPropertyValue(token).trim();
  return value || fallback;
}

function read(element: Element, name: ThemeName): ChartTheme {
  const styles = getComputedStyle(element);
  const palette = FALLBACK.palette.map((fallback, index) =>
    resolve(styles, `--chart-${index + 1}`, fallback),
  );
  return {
    name,
    palette,
    ink: resolve(styles, "--chart-ink", FALLBACK.ink),
    axis: resolve(styles, "--axis-ink", FALLBACK.axis),
    grid: resolve(styles, "--grid-line", FALLBACK.grid),
    surface: resolve(styles, "--chart-surface", FALLBACK.surface),
    tooltipBg: resolve(styles, "--tooltip-bg", FALLBACK.tooltipBg),
    tooltipInk: resolve(styles, "--tooltip-ink", FALLBACK.tooltipInk),
    tooltipLine: resolve(styles, "--tooltip-line", FALLBACK.tooltipLine),
    animate: !reduceMotion(),
  };
}

function currentName(): ThemeName {
  if (typeof document === "undefined") return "light";
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

let cached: ChartTheme | null = null;

/** The theme the document is currently displaying. */
export function chartTheme(): ChartTheme {
  const name = currentName();
  // The motion preference can change without the theme changing, so it is part
  // of what makes a cached theme stale.
  const animate = !reduceMotion();
  if (cached && cached.name === name && cached.animate === animate) {
    return cached;
  }
  cached =
    typeof document === "undefined"
      ? { name, ...FALLBACK, animate }
      : read(document.documentElement, name);
  return cached;
}

let exportCache: ChartTheme | null = null;

/**
 * The theme an exported image is drawn with, which is always the light one.
 *
 * An export is a document, and a document is read on paper or in a viewer that
 * knows nothing about the reader's screen preference. Capturing a dark chart
 * onto the white page the exporter composites against produced pale text on
 * white, which is the one outcome worse than either theme.
 */
export function exportChartTheme(): ChartTheme {
  if (exportCache) return exportCache;
  if (typeof document === "undefined") {
    exportCache = { name: "light", ...FALLBACK, animate: false };
    return exportCache;
  }
  const probe = document.createElement("div");
  probe.className = "chart-theme-light";
  probe.style.display = "none";
  document.body.append(probe);
  try {
    exportCache = { ...read(probe, "light"), animate: false };
  } finally {
    probe.remove();
  }
  return exportCache;
}

/**
 * Subscribes to theme changes.
 *
 * The theme lives on a data attribute rather than in React state — AppShell
 * writes it to `document.documentElement` — so an observer is what turns it
 * back into something a component can depend on.
 */
export function onThemeChange(listener: () => void): () => void {
  if (typeof document === "undefined") return () => undefined;

  const observer = new MutationObserver(() => {
    cached = null;
    listener();
  });
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  const motion =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
  const onMotion = () => {
    cached = null;
    listener();
  };
  motion?.addEventListener("change", onMotion);

  return () => {
    observer.disconnect();
    motion?.removeEventListener("change", onMotion);
  };
}
